class ByobProductsList extends HTMLElement {
  constructor() {
    super();
    this.handleClick = this.handleClick.bind(this);
    this.handleUpdate = this.handleUpdate.bind(this);
    this.handleCatalog = this.handleCatalog.bind(this);
    this.lastUpdate = null;
  }

  connectedCallback() {
    this.addEventListener('click', this.handleClick);
    document.addEventListener('byob:update', this.handleUpdate);
    if (this.dataset.source === 'offer') {
      document.addEventListener('byob:catalog', this.handleCatalog);
      document.dispatchEvent(new CustomEvent('byob:request-catalog'));
    }
    document.dispatchEvent(new CustomEvent('byob:request-state'));
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.handleClick);
    document.removeEventListener('byob:update', this.handleUpdate);
    document.removeEventListener('byob:catalog', this.handleCatalog);
  }

  formatPrice(minorUnits) {
    const amount = minorUnits / 100;
    const currency = window.Shopify?.currency?.active;
    if (!currency) return String(amount);
    return new Intl.NumberFormat(document.documentElement.lang || undefined, {
      style: 'currency',
      currency,
      maximumFractionDigits: Number.isInteger(amount) ? 0 : 2,
    }).format(amount);
  }

  readMeta() {
    try {
      return JSON.parse(this.querySelector('script[data-card-meta]')?.textContent || '{}');
    } catch (error) {
      return {};
    }
  }

  // The theme draws the cards from its product list at page load. Once the Easy Bundle offer is ready, the
  // offer decides: cards for products it does not have are hidden (they cannot be added to the box), and
  // offer products the list does not have are added. Sold-out products stay, since the offer leaves them out.
  handleCatalog(event) {
    const products = event.detail?.products ?? [];
    if (!products.length) return;

    const cards = new Map([...this.querySelectorAll('[data-product-id]')].map((card) => [card.dataset.productId, card]));
    const offerIds = new Set(products.map((product) => product.productId));
    const max = Number(this.dataset.max) || Infinity;

    for (const product of products) {
      const card = cards.get(product.productId);
      if (card) {
        card.dataset.variantId = product.variantId;
        card.hidden = false;
      } else if (this.querySelectorAll('[data-product-id]').length < max) {
        this.append(this.createCard(product));
      }
    }

    for (const [productId, card] of cards) {
      if (offerIds.has(productId) || card.dataset.available !== 'true') continue;
      console.warn('[byob-products] in stock but not in the Easy Bundle offer, hiding:', card.dataset.title);
      card.hidden = true;
    }

    this.dataset.loadedFrom = 'offer';
    console.info('[byob-products] cards synced with the Easy Bundle offer');
    if (this.lastUpdate) this.handleUpdate(this.lastUpdate);
  }

  createCard(product) {
    const template = this.querySelector('template[data-card-template]');
    const card = template.content.firstElementChild.cloneNode(true);
    const image = card.querySelector('img');
    const addButton = card.querySelector('[data-byob-add]');
    const thumb = product.imageUrl ? `${product.imageUrl}${product.imageUrl.includes('?') ? '&' : '?'}width=` : '';
    const { type: themeType, rating, count } = this.readMeta()[product.productId] ?? {};
    const type = themeType || product.type;

    card.dataset.productId = product.productId;
    card.dataset.variantId = product.variantId;
    card.dataset.title = product.title;
    card.dataset.thumbUrl = thumb ? `${thumb}160` : '';
    card.dataset.available = String(product.available);
    card.dataset.price = product.price;

    if (thumb) {
      image.src = `${thumb}480`;
      image.srcset = `${thumb}320 320w, ${thumb}480 480w, ${thumb}640 640w`;
      image.alt = product.title;
    } else {
      image.remove();
    }

    card.querySelector('[data-card-name]').textContent = product.title;
    card.querySelector('[data-card-price]').textContent = Number.isFinite(product.price) ? this.formatPrice(product.price) : '';

    if (type) {
      card.querySelector('[data-card-tag-text]').textContent = type;
      card.querySelector('[data-card-tag]').hidden = false;
    }

    if (rating > 0) {
      card.querySelector('[data-card-rating-text]').textContent = `${Number(rating).toFixed(1)}${count > 0 ? `(${count})` : ''}`;
      card.querySelector('[data-card-rating]').hidden = false;
    }

    addButton.textContent = product.available ? addButton.dataset.text : addButton.dataset.soldText;
    addButton.disabled = !product.available;
    return card;
  }

  handleClick(event) {
    const control = event.target.closest('[data-byob-add], [data-byob-inc], [data-byob-dec]');
    if (!control || control.disabled) return;

    const card = control.closest('[data-product-id]');
    if (!card) return;

    const { productId, variantId, title, thumbUrl } = card.dataset;

    if (control.matches('[data-byob-dec]')) {
      document.dispatchEvent(new CustomEvent('byob:remove', { detail: { productId } }));
      return;
    }

    document.dispatchEvent(
      new CustomEvent('byob:add', { detail: { productId, variantId, title, thumbUrl } })
    );
  }

  handleUpdate(event) {
    this.lastUpdate = event;
    const { counts = {}, isFull = false, limits = {} } = event.detail ?? {};
    const { max = Infinity, singleUnitIds = [] } = limits;

    for (const card of this.querySelectorAll('[data-product-id]')) {
      const count = counts[card.dataset.productId] ?? 0;
      const isAvailable = card.dataset.available === 'true';
      const addButton = card.querySelector('[data-byob-add]');
      const incButton = card.querySelector('[data-byob-inc]');
      const countLabel = card.querySelector('[data-byob-count]');

      card.classList.toggle('byob-products__card--in-box', count > 0);
      if (countLabel) countLabel.textContent = count;
      if (addButton) addButton.disabled = !isAvailable || isFull;
      const limit = singleUnitIds.includes(card.dataset.productId) ? 1 : max;
      if (incButton) incButton.disabled = isFull || count >= limit;
    }
  }
}

if (!customElements.get('byob-products-list')) {
  customElements.define('byob-products-list', ByobProductsList);
}
